import { FC, Fragment, useState } from 'react';
import { Button, Col, Form, Input, Result, Row, Space, Table } from 'antd';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import FRMVisitReportOfficeCollateralUnit from './VisitReportOfficePage.Collateral.Unit';
import FleetModal from '@/libs/antd/components/Modal';
import { FormAlert, useForm, useFormAlert } from '@/libs/antd/form';
import { useApp } from '@/App';
import { OfficeObservationCollateralAspect } from '@/libs/fleet-api/services/types';
import { useFRMVisitReportPageContext } from './VisitReportOfficePage';
import { useMutation } from '@tanstack/react-query';
import { submitFRMOfficeObservationCollateralAspect } from '@/libs/fleet-api/services/visit-reports';
import FleetTitle from '@/libs/antd/components/Title';
import rule from '@/libs/antd/rules';
import { useModals } from '@/components/modals/ModalProvider';

const FRMVisitReportOfficeCollateral: FC = () => {
	const { navigate } = useApp();
	const { openLKPDFModal } = useModals();
	const {
		officeObservation,
		surveyInformation,
		approvalHistories,
		mainInformation,
	} = useFRMVisitReportPageContext();
	const { collateral } = officeObservation ?? {};
	const { form, fieldName } = useForm<OfficeObservationCollateralAspect>();
	const [showConfirm, setShowConfirm] = useState(false);
	const [showSuccess, setShowSuccess] = useState(false);

	const submitFRMOfficeObservationCollateralAspectMutation = useMutation({
		mutationFn: submitFRMOfficeObservationCollateralAspect,
	});

	const { alerts, validate } = useFormAlert(form, null);

	return (
		<Form.Provider
			onFormFinish={async (name, { values }) => {
				if (!name) return;
				try {
					await submitFRMOfficeObservationCollateralAspectMutation.mutateAsync(
						values as OfficeObservationCollateralAspect,
					);
					setShowConfirm(false);
					setShowSuccess(true);
				} catch (error) {
					setShowConfirm(false);
					console.log(error);
				}
			}}
		>
			<FRMVisitReportOfficeCollateralUnit />

			{approvalHistories?.length ? (
				<Fragment>
					<FleetTitle>Approval History</FleetTitle>
					<Table
						dataSource={
							approvalHistories?.map((record, key) => {
								return { ...record, key };
							}) ?? []
						}
						columns={[
							{
								title: 'No',
								width: 1,
								align: 'center',
								dataIndex: 'no',
							},
							{
								title: 'Name',
								dataIndex: 'approvalBy',
							},
							{
								title: 'Date',
								dataIndex: 'approvalDate',
							},
							{
								title: 'Process',
								dataIndex: 'approvalProcess',
							},
							{
								title: 'Status',
								align: 'center',
								render: (_, record) => {
									return (
										<span
											className={`review-status ${record.approvalStatus.toLowerCase()}`}
										>
											{record.approvalStatus}
										</span>
									);
								},
							},
							{
								title: 'Approval Notes',
								dataIndex: 'approvalNote',
							},
						]}
						bordered
						pagination={false}
						style={{ marginBottom: 20 }}
					/>
				</Fragment>
			) : null}

			<FormAlert alerts={alerts} />
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				name="collateralNotes"
				initialValues={
					{
						collateralNotes: collateral?.collateralNotes,
						surveyId: surveyInformation?.id,
					} as OfficeObservationCollateralAspect
				}
			>
				<Row gutter={[20, 40]}>
					<Col span={24}>
						<Form.Item
							name={fieldName('surveyId')}
							noStyle
						/>

						<Form.Item
							name={fieldName('collateralNotes')}
							label="Informasi Tambahan"
							labelCol={{
								span: 5,
								style: {
									alignSelf: 'center',
								},
							}}
							wrapperCol={{
								style: {
									marginLeft: -5,
								},
							}}
							rules={[rule.required('Informasi Tambahan wajib diisi')]}
						>
							<Input.TextArea rows={3} />
						</Form.Item>
					</Col>
				</Row>
			</Form>

			<Space
				style={{
					display: 'flex',
					justifyContent: 'end',
					flex: 1,
					marginTop: 30,
				}}
				size="middle"
			>
				<Button
					type="default"
					onClick={() => {
						openLKPDFModal({
							type: 'lk-office',
							prospectId: mainInformation!.prospectId!,
						});
					}}
				>
					DOWNLOAD LK KANTOR
				</Button>
				<Button
					type="primary"
					style={{
						width: 200,
					}}
					loading={submitFRMOfficeObservationCollateralAspectMutation.isPending}
					onClick={async () => {
						validate();
						const isValid = await form?.validateFields();
						if (isValid) setShowConfirm(true);
					}}
				>
					SAVE
				</Button>
			</Space>

			<FleetModal
				open={Boolean(showConfirm)}
				styles={{ footer: { padding: 0 } }}
				okText="YA"
				cancelText="TIDAK"
				onCancel={() => setShowConfirm(false)}
				onOk={() => {
					form.submit();
				}}
				okButtonProps={{
					loading: submitFRMOfficeObservationCollateralAspectMutation.isPending,
				}}
			>
				<Result
					status="info"
					subTitle={
						<strong style={{ color: '#2F2F2F' }}>
							Apakah Anda yakin ingin menyimpan data ini? <br />
							Pastikan semua informasi sudah benar sebelum melanjutkan
						</strong>
					}
				/>
			</FleetModal>

			<FleetModal
				open={Boolean(showSuccess)}
				styles={{ footer: { padding: 0 } }}
				cancelButtonProps={{
					hidden: true,
				}}
				okText="NEXT"
				onOk={() => {
					setShowSuccess(false);
					navigate('FRMVisitReportListPage');
				}}
			>
				<Result
					status="success"
					title="Data Laporan Kunjungan Anda telah tersimpan"
					subTitle={<p>Silakan lanjutkan mengisi Laporan Kunjungan Lainnya</p>}
				/>
			</FleetModal>
		</Form.Provider>
	);
};

export default FRMVisitReportOfficeCollateral;
